import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMin, fmtDT } from "@/lib/format";
import { StatusBadge, SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer: "", sku: "", item_name: "", quantity: "", cartons: "", packing_type: "", priority: "normal" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreate = ["admin", "warehouse_manager", "supervisor"].includes(user?.role);

  const load = useCallback(async () => {
    const res = await api.get("/bills");
    setBills(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createBill = async () => {
    try {
      await api.post("/bills", {
        ...form,
        quantity: parseInt(form.quantity) || 0,
        cartons: form.cartons ? parseInt(form.cartons) : null,
      });
      toast.success("Bill created");
      setOpen(false);
      setForm({ customer: "", sku: "", item_name: "", quantity: "", cartons: "", packing_type: "", priority: "normal" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create bill");
    }
  };

  const filtered = bills.filter(
    (b) =>
      b.bill_no.toLowerCase().includes(search.toLowerCase()) ||
      b.customer.toLowerCase().includes(search.toLowerCase()) ||
      b.sku.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="bills-page">
      <SectionTitle
        right={
          canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-bill-button" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Plus className="w-4 h-4 mr-1" /> New Bill
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Create Bill / Order</DialogTitle><DialogDescription className="sr-only">Create a legacy bill tracking record</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Customer</Label>
                    <Input data-testid="bill-customer-input" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} />
                  </div>
                  <div>
                    <Label>SKU</Label>
                    <Input data-testid="bill-sku-input" placeholder="PET-BTL-500" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                  </div>
                  <div>
                    <Label>Item name</Label>
                    <Input data-testid="bill-item-input" placeholder="PET Bottle 500ml" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Quantity (pcs)</Label>
                    <Input data-testid="bill-quantity-input" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                  </div>
                  <div>
                    <Label>Cartons</Label>
                    <Input data-testid="bill-cartons-input" type="number" value={form.cartons} onChange={(e) => setForm({ ...form, cartons: e.target.value })} />
                  </div>
                  <div>
                    <Label>Packing type</Label>
                    <Input data-testid="bill-packing-type-input" value={form.packing_type} onChange={(e) => setForm({ ...form, packing_type: e.target.value })} />
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="bill-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button data-testid="bill-create-submit" onClick={createBill} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground mt-2">Create Bill</Button>
              </DialogContent>
            </Dialog>
          )
        }
      >
        Bill-wise Tracking
      </SectionTitle>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="bills-search-input" className="pl-9" placeholder="Search bill / customer / SKU" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="bills-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Bill No", "Customer", "SKU", "Qty", "Status", "Total Time", "Created"].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr
                key={b.bill_no}
                className="border-b border-border/50 hover:bg-accent/40 cursor-pointer transition-colors duration-150"
                onClick={() => navigate(`/bills/${b.bill_no}`)}
                data-testid={`bill-row-${b.bill_no}`}
              >
                <td className="px-4 py-3 metric font-bold text-primary">{b.bill_no}</td>
                <td className="px-4 py-3">{b.customer}</td>
                <td className="px-4 py-3 text-muted-foreground">{b.sku}</td>
                <td className="px-4 py-3 metric">{b.quantity.toLocaleString()}</td>
                <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                <td className="px-4 py-3 metric">{fmtMin(b.total_minutes)}</td>
                <td className="px-4 py-3 text-muted-foreground metric text-xs">{fmtDT(b.created_at)}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No bills found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
